import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MIN_REPLY = 20;

const PALETTE = {
  canvas: '#F9F6EF',
  navy: '#0A1F2E',
  ocean: '#5A6B73',
  sand: '#EFE6CF',
  teal: '#2CA8A0',
  gold: '#E8A830',
  border: '#C4B9A0',
};

const fmtMxCity = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    // Mexico City is UTC-6 (no DST since 2022).
    return d.toLocaleString('en-US', {
      timeZone: 'America/Mexico_City',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + ' CDT';
  } catch {
    return iso;
  }
};

const InfoState = ({ tone = 'info', headline, body }) => {
  const color = tone === 'danger' ? '#C0392B' : PALETTE.teal;
  return (
    <div
      className="max-w-xl mx-auto bg-white border rounded-2xl p-10 text-center"
      style={{ borderColor: PALETTE.sand }}
      data-testid={`reply-state-${tone}`}
    >
      <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color }} />
      <h1
        className="font-display italic text-2xl sm:text-3xl mb-3"
        style={{ color: PALETTE.navy }}
      >
        {headline}
      </h1>
      <p style={{ color: PALETTE.ocean }}>{body}</p>
    </div>
  );
};

// Internal - kept outside PartnerReply so React doesn't tear down the
// subtree on every parent render (no-unstable-nested-components).
const PartnerReplyHeader = () => (
  <header
    className="border-b"
    style={{ borderColor: PALETTE.sand, background: '#FFFFFF' }}
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center">
      <Link to="/" className="flex items-center gap-2" data-testid="reply-logo">
        <span
          className="font-display italic text-2xl"
          style={{ color: PALETTE.navy }}
        >
          Mundalo
        </span>
      </Link>
    </div>
  </header>
);

const PartnerReply = () => {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading|invalid|expired|used|valid|done
  const [conv, setConv] = useState(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Validate token on load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/partner-reply/${encodeURIComponent(token)}`);
        if (!r.ok) {
          if (!cancelled) setState('invalid');
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        if (data.state !== 'valid') {
          setState(data.state || 'invalid');
        } else {
          setConv(data.conversation);
          setState('valid');
          try {
            trackEvent('partner_reply_viewed', {
              partner_id: data.conversation?.partner?.id,
            });
          } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop - network failure → invalid */
        if (!cancelled) setState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (reply.length < MIN_REPLY || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const r = await fetch(
        `${API}/api/partner-reply/${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_body: reply }),
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.success) {
        const detail = (data.detail || '').toString();
        if (detail === 'used') { setState('used'); return; }
        if (detail === 'expired') { setState('expired'); return; }
        if (detail === 'invalid') { setState('invalid'); return; }
        setSubmitError(detail || 'Could not send your reply. Please try again.');
        return;
      }
      try {
        trackEvent('partner_reply_sent', {
          partner_id: conv?.partner?.id,
        });
      } catch (_) { /* noop */ }
      setState('done');
    } catch (_) { /* noop - network failure */
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render ----------

  let inner;
  if (state === 'loading') {
    inner = (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: PALETTE.teal }} />
      </div>
    );
  } else if (state === 'invalid') {
    inner = (
      <InfoState
        tone="danger"
        headline="This reply link is invalid."
        body="Please contact hello@mundalo.com if you need assistance."
      />
    );
  } else if (state === 'expired') {
    inner = (
      <InfoState
        tone="danger"
        headline="This reply link has expired."
        body="Please contact hello@mundalo.com and we will send you a new one."
      />
    );
  } else if (state === 'used') {
    inner = (
      <InfoState
        tone="info"
        headline="This reply link has already been used."
        body="If you need to send a follow-up please check your email for a new reply link or contact hello@mundalo.com."
      />
    );
  } else if (state === 'done') {
    inner = (
      <div
        className="max-w-xl mx-auto bg-white border-2 rounded-2xl p-10 text-center"
        style={{ borderColor: PALETTE.teal }}
        data-testid="reply-state-done"
      >
        <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: PALETTE.teal }} />
        <h1
          className="font-display italic text-3xl mb-3"
          style={{ color: PALETTE.navy }}
        >
          Your reply has been sent.
        </h1>
        <p style={{ color: PALETTE.ocean }}>
          The member will be notified.
        </p>
      </div>
    );
  } else {
    // valid → render the reply interface
    const memberName = conv?.member_first_name || 'Member';
    inner = (
      <div className="max-w-3xl mx-auto" data-testid="reply-page-valid">
        <p
          className="font-mono uppercase text-[10px] tracking-[0.22em] mb-3"
          style={{ color: PALETTE.teal }}
        >
          Mundalo Partner Reply
        </p>
        <h1
          className="font-display italic text-3xl sm:text-4xl mb-6"
          style={{ color: PALETTE.navy }}
          data-testid="reply-headline"
        >
          Reply to {memberName}
        </h1>

        <div
          className="border rounded-2xl bg-white px-6 py-5 mb-6"
          style={{ borderColor: PALETTE.sand }}
          data-testid="reply-thread"
        >
          <p
            className="font-mono uppercase text-[10px] tracking-[0.16em] mb-3"
            style={{ color: PALETTE.ocean }}
          >
            Conversation thread · {conv?.subject || ''}
          </p>
          <div className="space-y-3">
            {(conv?.messages || []).map((m) => {
              const isMember = m.sender_type === 'member';
              return (
                <div
                  key={m.message_id}
                  className={`flex ${isMember ? 'justify-end' : 'justify-start'}`}
                  data-testid={`reply-msg-${m.sender_type}`}
                >
                  <div
                    className="max-w-[78%] px-4 py-3 rounded-xl text-sm whitespace-pre-wrap"
                    style={{
                      background: PALETTE.canvas,
                      color: PALETTE.navy,
                      borderLeft: `3px solid ${isMember ? PALETTE.gold : PALETTE.teal}`,
                    }}
                  >
                    <p
                      className="font-mono uppercase text-[9px] tracking-[0.16em] mb-1"
                      style={{ color: PALETTE.ocean }}
                    >
                      {isMember ? 'Member' : 'You'} · {fmtMxCity(m.sent_at)}
                    </p>
                    {m.body}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={submit} data-testid="reply-form">
          <textarea
            required
            rows={6}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply here…"
            className="w-full px-4 py-3 bg-white border focus:outline-none focus:border-[#2CA8A0] transition resize-y"
            style={{ borderColor: PALETTE.border, color: PALETTE.navy }}
            data-testid="reply-textarea"
          />
          <p
            className="mt-2 text-xs font-mono uppercase tracking-wider"
            style={{ color: reply.length >= MIN_REPLY ? '#1A7F4F' : PALETTE.ocean }}
            data-testid="reply-char-counter"
          >
            {reply.length} / {MIN_REPLY} minimum characters
          </p>
          {submitError && (
            <p className="text-sm mt-2" style={{ color: '#C0392B' }} data-testid="reply-error">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={reply.length < MIN_REPLY || submitting}
            className="w-full sm:w-auto mt-4 px-10 py-3 font-bold uppercase tracking-wider rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition"
            style={{ background: PALETTE.gold, color: PALETTE.navy }}
            data-testid="reply-submit-btn"
          >
            {submitting ? 'Sending…' : 'Send Reply'}
          </button>
          <p
            className="mt-4 text-xs italic"
            style={{ color: PALETTE.ocean }}
          >
            Your reply will be sent securely through Mundalo. The member will
            be notified by email. This conversation is logged for quality assurance.
          </p>
        </form>

        {/* iter 144g - Self-serve profile image uploads. Uploads go into
            a pending queue for admin approval before going live on the
            public profile. */}
        <ProfileImagesSection token={token} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: PALETTE.canvas }} data-testid="partner-reply-root">
      <PartnerReplyHeader />
      <main className="px-4 sm:px-6 py-12">{inner}</main>
    </div>
  );
};

export default PartnerReply;

// =====================================================================
// iter 144g - Profile Images upload section. Token-gated, talks to:
//   GET  /api/partner-reply/:token/profile-images   (current live + pending)
//   POST /api/partner-reply/:token/upload-image     (queue a new upload)
// Cloudinary unsigned upload widget - same pattern as AdminPartners.
// =====================================================================
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dkflwujbm';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'mundalo_unsigned';

const _loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve();
    const existing = document.getElementById('cloudinary-widget-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const s = document.createElement('script');
    s.id = 'cloudinary-widget-script';
    s.src = 'https://upload-widget.cloudinary.com/global/all.js';
    s.onload = () => resolve();
    document.head.appendChild(s);
  });

const ProfileImagesSection = ({ token }) => {
  const [state, setState] = useState({ live: {}, pending: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');     // image_type currently uploading
  const [flash, setFlash] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/partner-reply/${token}/profile-images`);
      if (r.ok) setState(await r.json());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    _loadCloudinaryScript();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = (imageType) => {
    if (!window.cloudinary) return;
    setBusy(imageType);
    window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME, uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'], multiple: false, resourceType: 'image',
        folder: 'mundalo_partner_self_serve', maxFileSize: 5_000_000,
      },
      async (err, result) => {
        if (result?.event === 'success' && result?.info?.secure_url) {
          try {
            const r = await fetch(`${API}/api/partner-reply/${token}/upload-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                image_type: imageType,
                image_url: result.info.secure_url,
              }),
            });
            if (r.ok) {
              setFlash(`Your ${imageType} was submitted for admin review.`);
              await load();
            } else {
              setFlash('Upload could not be queued. Please try again.');
            }
          } catch (_) {
            setFlash('Network issue. Please try again.');
          } finally {
            setBusy('');
          }
        }
        if (result?.event === 'close' || result?.event === 'queues-end') {
          setBusy((cur) => (cur === imageType ? '' : cur));
        }
      },
    ).open();
  };

  const pendingByType = {};
  (state.pending || []).forEach((p) => { pendingByType[p.image_type] = p; });

  const Row = ({ label, imageType, helper }) => {
    const liveUrl = state.live?.[imageType] || '';
    const pending = pendingByType[imageType];
    return (
      <div className="bg-white border rounded-xl p-4" style={{ borderColor: PALETTE.sand }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {liveUrl ? (
              <img src={liveUrl} alt="" className="w-16 h-16 object-cover rounded-lg bg-[#F4ECD8]" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-[#F4ECD8] flex items-center justify-center text-xs text-[#5A6B73]">
                No image
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-mono uppercase text-[10px] tracking-[0.18em]" style={{ color: PALETTE.teal }}>
                {label}
              </p>
              <p className="text-xs mt-1" style={{ color: PALETTE.ocean }}>{helper}</p>
              {pending && (
                <p
                  className="text-xs mt-1.5 font-semibold inline-flex items-center gap-1"
                  data-testid={`profile-img-pending-${imageType}`}
                  style={{ color: '#8C5A14' }}
                >
                  ⏳ New version awaiting admin review
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => upload(imageType)}
            disabled={busy === imageType}
            data-testid={`profile-img-upload-${imageType}`}
            className="font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full border border-[#C4B9A0] hover:border-[#0A1F2E] text-[#0A1F2E] disabled:opacity-50"
          >
            {busy === imageType ? 'Uploading…' : (liveUrl || pending ? 'Replace' : 'Upload')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="mt-12 pt-10 border-t" style={{ borderColor: PALETTE.sand }} data-testid="profile-images-section">
      <p className="font-mono uppercase text-[11px] tracking-[0.22em] mb-2" style={{ color: PALETTE.teal }}>
        Manage your profile images
      </p>
      <h3 className="font-display italic text-2xl mb-2" style={{ color: PALETTE.navy }}>
        Update what members see.
      </h3>
      <p className="text-sm mb-6" style={{ color: PALETTE.ocean }}>
        Uploads here go to a Mundalo admin for review and go live on your public profile once approved.
        Replacing an image queues a new pending submission - the live image stays in place until approval.
      </p>

      {flash && (
        <p
          className="mb-4 text-sm px-3 py-2 rounded-md"
          style={{ background: PALETTE.sand, color: PALETTE.navy }}
          data-testid="profile-img-flash"
        >
          {flash}
        </p>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: PALETTE.ocean }}>Loading…</p>
      ) : (
        <div className="space-y-3">
          <Row label="Thumbnail (partner card)" imageType="thumbnail" helper="The circular avatar shown on your partner card in the directory." />
          <Row label="Logo (profile header)"   imageType="logo"      helper="Shown at the top of your full public profile page." />
          <Row label="Gallery image"           imageType="gallery"   helper="One additional photo shown on your profile alongside the thumbnail." />
        </div>
      )}
    </section>
  );
};
